// S4.4, OFFICE HOST — /quality/inspections/$id (spec 07 §4.4: "office fallback
// identical"). This file is a HOST, not a second implementation: it fetches,
// applies the §7 gate, and renders the ONE `InspectionRun` component the floor
// renders, with the office's write port.
//
// It also carries the one thing the floor screen does not: §5's `cancel`. §7
// puts cancellation on qc / facility_admin / group_admin and NOT on
// floor_worker — cancelling somebody's in-flight check is a supervisor's act
// with a mandatory reason, and putting it on a station tablet beside "Fail"
// would make it a mis-tap.

import { shortId } from "../../../../lib/short-id";
import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { CardSkeleton, ErrorState, ForbiddenState } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import { useInventoryPermissions } from "../../inventory/hooks/permissions";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import {
  useCancelQualityInspection,
  useQualityFailureCauses,
  useQualityInspection,
} from "../hooks/quality-queries";
import { InspectionRun } from "../run/inspection-run";
import { useOfficeRunPort } from "../run/use-office-run-port";
import { samplingEchoText } from "./inspections";

export function InspectionDetailScreen() {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/quality/inspections/$id" });
  const perms = useQualityPermissions();
  // Module 06's mirror, for the ONE module-06 control this screen hosts: the
  // §9.6 re-measure the §4.4 sheet offers in place when a claimed-only length
  // is what is blocking a cut. Same role feed, one fetch (`useRoleCodesFeed`).
  const inventoryPerms = useInventoryPermissions();
  const refused = isQualityReadRefused(perms, "quality.inspection.read");

  const detail = useQualityInspection(id, !refused);
  const causes = useQualityFailureCauses(!refused);
  const port = useOfficeRunPort();
  const cancel = useCancelQualityInspection();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (refused) {
    return <ForbiddenState title={t("quality.access.title")} body={t("quality.access.queue")} />;
  }
  if (detail.isPending) {
    return <CardSkeleton lines={8} label={t("quality.common.loading")} />;
  }
  if (detail.error !== null) {
    return (
      <ErrorState
        title={t("quality.run.loadError")}
        retryLabel={t("quality.common.retry")}
        requestIdLabel={t("quality.common.requestId")}
        requestId={requestIdOf(detail.error)}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  const inspection = detail.data.inspection;
  const canCancel =
    perms.has("quality.inspection.cancel") &&
    (inspection.status === "open" || inspection.status === "in_progress");

  return (
    <section className="quality-detail" data-testid="quality-detail">
      <PageHeader
        crumbs={[{ label: t("quality.queue.title"), to: "/quality/inspections" }]}
        title={t("quality.run.title")}
      />

      {/* 07-D — the §2.5 echo (D-192): a receiving inspection born of a
          sampler says what the plan was, on the read the operator opened,
          with the cohort's short ref beside it so the sibling rows in the
          queue read as one decision. Absent when no sampler ran — every
          in-process/final row and the inspect_all sweep's manual rows. */}
      {inspection.sampling != null ? (
        <p className="quality-note" data-testid="quality-detail-sampling">
          {t("quality.detail.samplingLabel")} {samplingEchoText(t, inspection.sampling)}
          {inspection.sample_cohort_id != null ? (
            <>
              {" · "}
              <span data-testid="quality-detail-cohort">
                {t("quality.queue.cohortBadge", {
                  ref: shortId(inspection.sample_cohort_id),
                })}
              </span>
            </>
          ) : null}
        </p>
      ) : null}

      <InspectionRun
        detail={detail.data}
        causes={causes.data ?? []}
        canRecord={perms.has("quality.inspection.record")}
        // §4.4's sheet grants (07-B-3), from the one mirror. Fail-open when the
        // feed itself FAILED — the mirror is a display aid; the server renders
        // its own refusal (role-codes.ts's rule, same as the floor's canRecord).
        failureGrants={{
          canRework: perms.has("quality.resolution.rework") || perms.isFeedError,
          canDispose: perms.has("quality.resolution.dispose") || perms.isFeedError,
          canQuarantine: perms.has("quality.hold.place") || perms.isFeedError,
          // 07-C's two. `quality.cut.execute` is §7's own row; the re-measure
          // is MODULE 06's code on module 06's route, so it comes from module
          // 06's own mirror rather than from a second transcription of a table
          // this area does not own (the perms:mirror gate checks each mirror
          // against its own namespace, and inventing an `inventory.*` cell in
          // the quality map would be a copy nothing binds).
          canCut: perms.has("quality.cut.execute") || perms.isFeedError,
          canRemeasure: inventoryPerms.has("inventory.scan.locate") || perms.isFeedError,
        }}
        port={port}
        shell="office"
        onApplied={() => {
          void detail.refetch();
        }}
      />

      {/* D-118 — absent for a role without the grant, not disabled. */}
      {canCancel ? (
        <div className="quality-toolbar" data-testid="quality-cancel">
          {cancelOpen ? (
            <>
              <Field label={t("quality.run.cancelReason")} wide>
                <input
                  value={reason}
                  data-testid="quality-cancel-reason"
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </Field>
              <Button
                variant="danger"
                data-testid="quality-cancel-confirm"
                disabled={reason.trim().length === 0}
                busy={cancel.isPending}
                busyLabel={t("quality.common.saving")}
                onClick={() => {
                  cancel.mutate(
                    { id, reason: reason.trim() },
                    {
                      onSuccess: () => {
                        setCancelOpen(false);
                        void detail.refetch();
                      },
                    },
                  );
                }}
              >
                {t("quality.run.cancelConfirm")}
              </Button>
            </>
          ) : (
            <Button
              variant="danger"
              data-testid="quality-cancel-open"
              onClick={() => {
                setCancelOpen(true);
              }}
            >
              {t("quality.run.cancelOpen")}
            </Button>
          )}
          {cancel.isError ? (
            <p className="quality-error-note" role="alert" data-testid="quality-cancel-failed">
              {t("quality.run.cancelFailed")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
